import {
  getAllSuppliersWoLoader,
  getBranchesDataSharingWoLoader,
  getClinicBranchesWithClinicIdWoLoader,
  getClinicDetailsWoLoader,
  getClinicFacilitiesWoLoader,
  getClinicHolidaysWoLoader,
  getClinicInpatientsWoLoader,
  getClinicLocationsWoLoader,
  getClinicNotificationWoLoader,
  getClinicOpeningHoursWoLoader,
  getClinicPaymentMethodsWoLoader,
  getClinicRolesWoLoader,
  getClinicStocksWoLoader,
  getClinicVetsUnavailabilityWoLoader,
  getServicesWoLoader,
} from "../redux/reducers/clinicSlice";
import { getClinicVetsWoLoader } from "../redux/reducers/vetSlice";
import {
  MANDATE_STORAGE_KEY,
  patchMandateSession,
  readMandateSession,
} from "./mandateSessionStorage";

/**
 * Calls only data-sharing API and stores `dataSharing` on mandate session.
 * Call this before screen APIs (serviceData / inventryData / petData params).
 *
 * @param {(action: unknown) => unknown} dispatch Redux dispatch
 * @param {string | null | undefined} [_role] unused; kept for call-site compatibility
 * @returns {Promise<object>} dataSharing object (first branch entry)
 */
export async function fetchAndStoreMandateSession(dispatch, _role) {
  const dataShareRes = await dispatch(getBranchesDataSharingWoLoader());
  const sharing = dataShareRes?.payload?.[0] ?? {};
  if (dataShareRes?.payload) {
    const prev = readMandateSession() ?? {};
    sessionStorage.setItem(
      MANDATE_STORAGE_KEY,
      JSON.stringify({
        ...prev,
        dataSharing: sharing,
        // Do not bump lastUpdatedAt — that tracks the rest of mandate APIs
        ...(prev.lastUpdatedAt ? { lastUpdatedAt: prev.lastUpdatedAt } : {}),
      }),
    );
  }
  return sharing;
}

/** Normalize list payloads: API may return `[]` or `{ data: [] }`. */
const toList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/** Yield so the UI can paint between mandate batches. */
const yieldToMain = () =>
  new Promise((resolve) => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => resolve(), { timeout: 120 });
    } else {
      setTimeout(resolve, 0);
    }
  });

/**
 * Run async jobs with a concurrency cap (avoids saturating the browser connection pool).
 * @template T
 * @param {Array<() => Promise<T>>} jobs
 * @param {number} concurrency
 * @returns {Promise<Array<PromiseSettledResult<T>>>}
 */
async function runWithConcurrency(jobs, concurrency = 3) {
  const results = new Array(jobs.length);
  let next = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, jobs.length) },
    async () => {
      while (next < jobs.length) {
        const i = next++;
        try {
          results[i] = { status: "fulfilled", value: await jobs[i]() };
        } catch (reason) {
          results[i] = { status: "rejected", reason };
        }
      }
    },
  );

  await Promise.all(workers);
  return results;
}

const settledPayload = (result) =>
  result?.status === "fulfilled" ? result.value : null;

/**
 * Calls remaining mandate APIs and patches `mandateData` session.
 * Uses already-stored `dataSharing` for serviceData / inventryData.
 * Notifications stay Redux-only (not written to session).
 *
 * Loads in prioritized batches with limited concurrency, patching session
 * after each batch so data is available progressively without blocking the UI.
 *
 * Call after the current screen has fired its APIs.
 * Replaces the old full `fetchAndStoreMandateSession` usage in App / Login deferred path.
 *
 * @param {(action: unknown) => unknown} dispatch Redux dispatch
 * @param {string | null | undefined} _role unused; kept for call-site compatibility
 */
export async function fetchAndPatchMandateSession(dispatch, _role) {
  const sharing = readMandateSession()?.dataSharing ?? {};
  const serviceData = sharing?.serviceData ?? false;
  const inventryData = sharing?.inventryData ?? false;
  const vetData = sharing?.vetData ?? false;

  // Redux only — do not write to mandate session
  void dispatch(getClinicNotificationWoLoader());

  if (Object.keys(sharing).length > 0) {
    patchMandateSession({ dataSharing: sharing });
  }

  /**
   * Priority batches: critical org data first, heavy lists next, secondary last.
   * Each entry: [sessionKey, thunkFn, options?]
   * options.asObject — store payload as object (clinic), else as list
   */
  const batches = [
    // 1) Org / access — needed widely (branches, roles, clinic, hours)
    [
      ["clinic", () => dispatch(getClinicDetailsWoLoader()), { asObject: true }],
      [
        "clinicBranches",
        () => dispatch(getClinicBranchesWithClinicIdWoLoader()),
      ],
      ["clinicRoles", () => dispatch(getClinicRolesWoLoader())],
      ["clinicHours", () => dispatch(getClinicOpeningHoursWoLoader())],
    ],
    // 2) Core catalogs used on most screens
    [
      [
        "vets",
        () => dispatch(getClinicVetsWoLoader(`?type=all&vetData=${vetData}`)),
      ],
      [
        "services",
        () => dispatch(getServicesWoLoader(`?serviceData=${serviceData}`)),
      ],
      [
        "stocks",
        () => dispatch(getClinicStocksWoLoader(`?inventryData=${inventryData}`)),
      ],
      ["locations", () => dispatch(getClinicLocationsWoLoader())],
    ],
    // 3) Secondary / less urgently needed
    [
      ["facilities", () => dispatch(getClinicFacilitiesWoLoader())],
      ["paymentMethods", () => dispatch(getClinicPaymentMethodsWoLoader())],
      ["clinicHolidays", () => dispatch(getClinicHolidaysWoLoader())],
      [
        "vetUnavailability",
        () => dispatch(getClinicVetsUnavailabilityWoLoader()),
      ],
      ["inpatients", () => dispatch(getClinicInpatientsWoLoader("?type=all"))],
      ["suppliers", () => dispatch(getAllSuppliersWoLoader())],
    ],
  ];

  for (let b = 0; b < batches.length; b += 1) {
    const batch = batches[b];
    const settled = await runWithConcurrency(
      batch.map(([, run]) => run),
      3,
    );

    const patch = {};
    batch.forEach(([key, , opts], i) => {
      const res = settledPayload(settled[i]);
      const payload = res?.payload;

      if (opts?.asObject) {
        if (payload && payload !== false) {
          patch[key] = payload ?? {};
        }
        return;
      }

      // Keep previous semantics: patch when payload is present (incl. empty list)
      if (payload != null) {
        patch[key] = toList(payload);
      }
    });

    if (Object.keys(patch).length > 0) {
      patchMandateSession(patch);
    }

    // Let the main thread breathe between batches (skip after last)
    if (b < batches.length - 1) {
      await yieldToMain();
    }
  }
}
